import * as turf from '@turf/turf'
import { Stage } from 'konva/lib/Stage'

import { IPosition } from '~/client/graph'

export const MAX_RELATIVE_POSITION = 100
const MIN_RELATIVE_POSITION = 0
const MAP_EDGE_OFFSET = 2

export function sitemapMousePointTo(
  stage: Stage,
  containerWidth: number,
  containerHeight: number,
  pointerPosition?: IPosition,
  scale?: number,
) {
  const currentPointerPosition = pointerPosition || stage.getPointerPosition()
  const stageScale = scale || stage.scaleX()

  const stageWidth = stage.width()
  const stageHeight = stage.height()
  const containerOffsetX = (stageWidth - containerWidth) / 2
  const containerOffsetY = (stageHeight - containerHeight) / 2

  return {
    x: (currentPointerPosition.x - stage.x()) / stageScale - containerOffsetX,
    y: (currentPointerPosition.y - stage.y()) / stageScale - containerOffsetY,
  }
}

export function validatePosition(pos: IPosition): IPosition {
  return {
    x: Math.min(Math.max(MIN_RELATIVE_POSITION, pos.x), MAX_RELATIVE_POSITION),
    y: Math.min(Math.max(MIN_RELATIVE_POSITION, pos.y), MAX_RELATIVE_POSITION),
  }
}

export function getCorrectModalOffsetPosition(
  newLeft: number,
  newTop: number,
  maxX: number,
  maxY: number,
  modalWidth: number,
  modalHeight: number,
): IPosition {
  const newRight = newLeft + modalWidth
  const newBottom = newTop + modalHeight

  let left = newLeft
  if (newRight > maxX) {
    left = left - (newRight - maxX) - MAP_EDGE_OFFSET
  }

  let top = newTop
  if (newBottom > maxY) {
    top = top - (newBottom - maxY) - MAP_EDGE_OFFSET
  }

  return {
    x: Math.max(MIN_RELATIVE_POSITION, left),
    y: Math.max(MIN_RELATIVE_POSITION, top),
  }
}

// Each part is its rings, open: the outline (or a line's path) first, then any holes.
export interface IFeatureParts {
  parts: GeoJSON.Position[][][]
  isClosed: boolean
}

function toPartGeometry(
  part: GeoJSON.Position[][],
  isClosed: boolean,
): GeoJSON.Polygon | GeoJSON.LineString {
  return isClosed
    ? { type: 'Polygon', coordinates: part.map(ring => [...ring, ring[0]]) }
    : { type: 'LineString', coordinates: part[0] }
}

// Shape coordinates store rings open and close them on read. Source files can repeat the
// closing vertex more than once, so every trailing copy of the first vertex is dropped.
function openRing(ring: GeoJSON.Position[]): GeoJSON.Position[] {
  let end = ring.length
  while (
    end > 1 &&
    ring[end - 1][0] === ring[0][0] &&
    ring[end - 1][1] === ring[0][1]
  ) {
    end--
  }
  return ring.slice(0, end)
}

function largestFirst(
  parts: GeoJSON.Position[][][],
  isClosed: boolean,
): GeoJSON.Position[][][] {
  const sizeOf = (part: GeoJSON.Position[][]): number => {
    const geometry = toPartGeometry(part, isClosed)
    return isClosed ? turf.area(geometry) : turf.length(turf.feature(geometry))
  }
  return parts
    .map(part => ({ part, size: sizeOf(part) }))
    .sort((a, b) => b.size - a.size)
    .map(({ part }) => part)
}

// Every part of a feature geometry, largest first by ground area (by length for lines).
export function getFeatureParts(
  geometry: GeoJSON.Geometry | undefined,
): IFeatureParts | undefined {
  switch (geometry?.type) {
    case 'Polygon':
      return { parts: [geometry.coordinates.map(openRing)], isClosed: true }
    case 'MultiPolygon':
      return {
        parts: largestFirst(
          geometry.coordinates.map(polygon => polygon.map(openRing)),
          true,
        ),
        isClosed: true,
      }
    case 'LineString':
      return { parts: [[geometry.coordinates]], isClosed: false }
    case 'MultiLineString':
      return {
        parts: largestFirst(
          geometry.coordinates.map(line => [line]),
          false,
        ),
        isClosed: false,
      }
    default:
      return undefined
  }
}

// A point on the ground the shape covers. The centre of mass of a concave part, or of one with
// a hole such as a courtyard, can fall outside it; then the widest stretch of a horizontal line
// through it that lies inside the part is used instead. Lines keep their centre of mass.
export function getInteriorAnchor(
  geometry: GeoJSON.Polygon | GeoJSON.LineString,
): GeoJSON.Position {
  const center = turf.centerOfMass(geometry).geometry.coordinates
  if (
    geometry.type !== 'Polygon' ||
    turf.booleanPointInPolygon(center, geometry)
  ) {
    return center
  }
  const [, minLat, , maxLat] = turf.bbox(geometry)
  return (
    widestInsideSpan(geometry, center[1]) ??
    widestInsideSpan(geometry, (minLat + maxLat) / 2) ??
    center
  )
}

// Midpoint of the widest span of the line `lat = latitude` inside the polygon. Crossings of
// every ring, holes included, pair up even-odd into inside spans.
function widestInsideSpan(
  polygon: GeoJSON.Polygon,
  latitude: number,
): GeoJSON.Position | undefined {
  const crossings: number[] = []
  polygon.coordinates.forEach(ring => {
    for (let i = 0; i < ring.length - 1; i++) {
      const [x1, y1] = ring[i]
      const [x2, y2] = ring[i + 1]
      if (y1 > latitude !== y2 > latitude) {
        crossings.push(x1 + ((latitude - y1) * (x2 - x1)) / (y2 - y1))
      }
    }
  })
  crossings.sort((a, b) => a - b)
  let widest: { width: number; middle: number } | undefined
  for (let i = 0; i + 1 < crossings.length; i += 2) {
    const width = crossings[i + 1] - crossings[i]
    if (!widest || width > widest.width) {
      widest = { width, middle: (crossings[i] + crossings[i + 1]) / 2 }
    }
  }
  return widest && [widest.middle, latitude]
}

// The anchor must describe the part the outline is taken from. turf's centerOfMass has no
// MultiPolygon branch and falls back to the convex hull of every part, which lands in the gap
// between them or inside a part other than the outlined one.
export function getFeatureAnchor(geometry: GeoJSON.Geometry): GeoJSON.Position {
  const featureParts = getFeatureParts(geometry)
  return featureParts
    ? getInteriorAnchor(
        toPartGeometry(featureParts.parts[0], featureParts.isClosed),
      )
    : turf.centerOfMass(geometry).geometry.coordinates
}
